import {useEffect, useState} from "react";
import {listLocationsService} from "../../../services/locationService";
import {capitalizeFirstLetter} from "../../../helpers/helpers";
import Grid from "@mui/material/Grid";
import MDBox from "../../../components/MDBox";
import Card from "@mui/material/Card";
import MDTypography from "../../../components/MDTypography";
import {listProductsService} from "../../../services/productService";
import {API_URL, APP_URL} from "../../../config";
import IconButton from "@mui/material/IconButton";
import AmountControl from "../../../components/AmountControl/AmountControl";
import {storeSaleService} from "../../../services/saleService";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {listDoctorsService} from "../../../services/doctorService";
import {listPatientsService} from "../../../services/patientService";
import {getSettingService} from "../../../services/settingsService";

const useNewSale = () => {

    const authState = useSelector((state) => state.authReducer);

    let navigate = useNavigate();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [patientId, setPatientId] = useState(-1);
    const [patients, setPatients] = useState([]);
    const [products, setProducts] = useState([]);
    const [saleProducts, setSaleProducts] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');

    const [snackbarConfig, setSnackbarConfig] = useState({
        color: 'info',
        title: '',
        message: '',
        icon: 'notifications'
    });

    const [show, setShow] = useState(false);
    const toggleSnackbar = () => setShow(!show);

    useEffect(() => {

        const init = async () => {
            setIsLoading(true);
            await loadPatients();
            await loadProducts();
            setIsLoading(false);
        };

        init();

    }, []);

    const loadProducts = async () => {

        try {

            const response = await listProductsService({
                sort: 'name',
                direction: 'asc',
                active: true
            });

            if(response.data.data.length > 0)
                setProducts(response.data.data);

        } catch (error) {

            setSnackbarConfig({
                message: "Ocurrió un error cargando los productos",
                icon: 'cancel',
                title: 'Productos',
                color: 'warning'
            });

            setShow(true);

        }
    };

    const loadPatients = async () => {

        try {

            const response = await listPatientsService({
                sort: 'name',
                direction: 'asc',
                active: true
            });

            if(response.data.data.length > 0)
                setPatients([{id: -1, label: "Selecciona el paciente"}, ...response.data.data.map(patient => ({id: patient.id, label: capitalizeFirstLetter(`${patient.name} ${patient.last_name || ''} ${patient.mother_last_name || ''}`)}))]);

        } catch (error) {

            setSnackbarConfig({
                message: "Ocurrió un error cargando los pacientes",
                icon: 'cancel',
                title: 'Pacientes',
                color: 'warning'
            });

            setShow(true);

        }
    };

    const handleSubmit = async () => {

        setIsSubmitting(true);

        try {

            const saleData = {
                patient_id: patientId.id,
                sale_items: saleProducts,
                location_id: authState.locationId
            };

            // Send data to API
            await storeSaleService(saleData);

            // Reset the form
            setPatientId(-1);
            setSaleProducts([]);

            setSnackbarConfig({
                message: 'Venta registrada exitosamente',
                icon: 'notifications',
                title: 'Ventas',
                color: 'success'
            });

            setShow(true);

        } catch (error) {

            setSnackbarConfig({
                message: error.response.data.message,
                icon: 'cancel',
                title: 'Ventas',
                color: 'warning'
            });

            setShow(true);
        }

        setIsSubmitting(false);
    };

    const addToSale = (product, itemPrice) => {
        setSaleProducts((prevSaleProducts) => {
            const existingItem = prevSaleProducts.find((item) => item.product.id === product.id);

            if (existingItem) {
                return prevSaleProducts.map((item) =>
                    item.product.id === product.id
                        ? {...item, quantity: item.quantity + 1}
                        : item
                );
            } else {
                return [...prevSaleProducts, {product, quantity: 1, price: itemPrice}];
            }
        });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handlePatientIdChange = (event, value) => {
        setPatientId(value);
    };

    const calculateTotal = () => {
        return calculateSubtotal() - calculateDiscount();
    };

    const calculateSubtotal = () => {
        return saleProducts.reduce((total, product) => total + product.price * product.quantity, 0);
    };

    const calculateDiscount = () => {
        return saleProducts.reduce((total, product) => total + product.product.discount_amount * product.quantity, 0);
    };

    const renderLineItems = () => {

        return saleProducts.map((product) => {
            const imageUrl = `${APP_URL}/storage/products/${product.product.image_name}`;

            return (
                <MDBox p={2} sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', borderBottomStyle: 'dashed', borderBottomWidth: 1, borderBottomColor: '#ececec'}}>
                    <MDBox sx={{backgroundColor: '#f7f7f7', borderStyle: 'solid', borderWidth: 1, borderColor: '#f7f7f7', borderRadius: 2, width: 70, height: 70, position: 'relative', marginRight: 2}}>
                        <img src={imageUrl} alt={product.product.name} style={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover'}} />
                    </MDBox>
                    <MDBox sx={{flexGrow: 1}}>
                        <MDTypography variant="h6">{product.product.name}</MDTypography>
                        <MDBox sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
                            <MDTypography variant="body2" sx={{fontSize: 14}}>${product.price} MXN</MDTypography>
                            <AmountControl
                                quantity={product.quantity}
                                onIncrement={() => incrementItemQuantity(product.product.id)}
                                onDecrement={() => decrementItemQuantity(product.product.id)}
                            />
                        </MDBox>
                    </MDBox>
                </MDBox>
            );
        });
    };

    const renderProducts = () => {
        const filteredProducts = products.filter(product =>
            product.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return filteredProducts.map((product) => {
            const imageUrl = `${APP_URL}/storage/products/${product.image_name}`;

            return (
                <Grid key={product.id} item xs={12} lg={3}>
                    <MDBox>
                        <Card id={product.id} sx={{padding: 2}} onClick={() => addToSale(product, product.price)}>
                            <MDBox sx={{backgroundColor: '#f7f7f7', borderStyle: 'solid', borderWidth: 1, borderColor: '#f7f7f7', borderRadius: 2, width: '100%', paddingTop: '100%', position: 'relative'}}>
                                <img src={imageUrl} alt={product.name} style={{
                                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover'}} />
                            </MDBox>
                            <MDTypography mt={1} variant="h6">{product.name}</MDTypography>
                        </Card>
                    </MDBox>
                </Grid>
            );
        });
    };

    const incrementItemQuantity = (productId) => {
        setSaleProducts((prevSaleProducts) =>
            prevSaleProducts.map((saleProduct) =>
                saleProduct.product.id === productId ? { ...saleProduct, quantity: saleProduct.quantity + 1 } : saleProduct
            )
        );
    };

    const decrementItemQuantity = (productId) => {
        setSaleProducts((prevSaleProducts) =>
            prevSaleProducts
                .map((saleProduct) =>
                    saleProduct.product.id === productId
                        ? { ...saleProduct, quantity: saleProduct.quantity - 1 }
                        : saleProduct
                )
                .filter((saleProduct) => saleProduct.quantity > 0)
        );
    };

    return {
        isLoading,
        isSubmitting,
        products,
        saleProducts,
        patientId,
        patients,
        show,
        snackbarConfig,
        navigate,
        addToSale,
        handlePatientIdChange,
        handleSubmit,
        handleSearch,
        calculateTotal,
        calculateSubtotal,
        calculateDiscount,
        searchTerm,
        toggleSnackbar,
        renderProducts,
        renderLineItems
    };
};

export default useNewSale;
